/*
Copyright 2025 iodyne.  All rights reserved.
iodyne Proprietary/Confidental: INTERNAL USE ONLY
*/

import { Link } from "@tanstack/react-router"
import { PlayIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface BreadcrumbItem {
    id:string,
    title:string,
    count?:number,
    child?:boolean,
    link_path?:string
}

/**
 * Decode and validate breadcrumbs in search parameters. 
 * @param search 
 * @returns 
 */
export function validateSearchBreadcrumbs(search: Record<string, unknown>): { bc?: BreadcrumbItem[] } {
    var search_params = {};
    if (search.bc) {
        search_params["bc"] = (search.bc as BreadcrumbItem[]).map((item: BreadcrumbItem) => {
            if (!item.link_path || item.link_path.startsWith("/")) {
                return item;
            }
            return null;
        }).filter((item) => item !== null)
    }
    return search_params;
}

export enum BreadcrumbVariant {
    default,
    simpleBackButton
}

export function BreadcrumbNav({
    title,
    title_count,
    breadcrumbs,
    className,
    variant = BreadcrumbVariant.default
}:{
    title?:string,
    title_count?:number,
    breadcrumbs?:BreadcrumbItem[],
    className?:string,
    variant?:BreadcrumbVariant
}) {
    const last_bc = (breadcrumbs && breadcrumbs.length > 0) ? breadcrumbs[breadcrumbs.length-1] : undefined;

    return <div className={cn("flex flex-row justify-between items-end mt-2", className)}>
        <div className="flex flex-col justify-end grow">
            <div className="flex flex-row space-x-2 items-end pb-3 grow font-bold">
                {variant == BreadcrumbVariant.default && breadcrumbs?.map((item, idx) => {
                    return <div 
                            key={item.id} 
                            className="flex flex-row items-center space-x-2 uppercase text-secondary font-[12px] aria-current:text-muted"
                            aria-current={idx == breadcrumbs.length-1}>
                        {item.child && <div>
                            <PlayIcon 
                                className="fill-secondary aria-current:fill-muted" width={10}
                                aria-current={idx == breadcrumbs.length-1} />
                        </div>}
                        <div className="flex flex-row space-x-1 items-center">
                            <div
                                data-link={item.link_path}
                                className="data-link:cursor-pointer">
                                {!item.link_path && item.title}
                                {item.link_path && <Link to={item.link_path}>{item.title}</Link>}
                            </div>
                            {item.count && <div className="text-xs">({item.count})</div>}
                        </div>
                    </div>
                })}
                { variant == BreadcrumbVariant.simpleBackButton && last_bc && <div 
                    className="flex flex-row items-center space-x-3 uppercase font-[12px] text-muted">
                     <PlayIcon className="fill-muted rotate-180" width={10} />
                    <div className="flex flex-row space-x-1 items-center">
                            <div
                                data-link={last_bc.link_path}
                                className="data-link:cursor-pointer">
                                {!last_bc.link_path && last_bc.title}
                                {last_bc.link_path && <Link to={last_bc.link_path}>{last_bc.title}</Link>}
                            </div>
                            {last_bc.count && <div className="text-xs">({last_bc.count})</div>}
                        </div>
                </div>}
            </div>
        </div>
       {title && <div className="flex flex-row space-x-1 items-center">
            <div className="text-[48px] text-iodyneDustyVioletLight">
                {title}
            </div>
            {title_count && <div className="pt-5 text-secondary">({title_count})</div>}
        </div>}
    </div>
}